import { useEffect, useState } from "react";
import { BookOpen, BriefcaseBusiness, CircleDollarSign, Users, Wallet, X } from "lucide-react";
import { api, today } from "@/util";

const TYPES = [
  { key: "tour", label: "Yeni tur", description: "Operasyon ve katılımcı planla", icon: BriefcaseBusiness, testid: "modal-tour-option" },
  { key: "income", label: "Gelir / tahsilat", description: "Kasaya gelen hareketi kaydet", icon: CircleDollarSign, testid: "modal-income-option" },
  { key: "expense", label: "Gider / ödeme", description: "Çıkan hareketi kaydet", icon: BookOpen, testid: "modal-expense-option" },
  { key: "customer", label: "Yeni müşteri", description: "Cari hesap kaydı aç", icon: Users, testid: "modal-customer-option" },
  { key: "bank", label: "Banka hesabı", description: "Yeni banka ya da kasa", icon: Wallet, testid: "modal-bank-option" },
];

const INCOME_CATEGORIES = ["Tur Satışı", "Tahsilat", "Transfer", "Diğer Gelir"];
const EXPENSE_CATEGORIES = ["Tedarikçi", "Operasyon", "Pazarlama", "Kira", "Vergi", "Diğer Gider"];

function Field({ label, children, span }) {
  return (
    <label className={`form-field ${span === 2 ? "span-2" : ""}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

export default function NewRecordModal({ open, initial, user, accounts, tours, onClose, onCreated }) {
  const isManager = user?.role === "Yönetici";
  const [mode, setMode] = useState(initial || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    setMode(initial || null);
    setError("");
    setSubmitting(false);
    setForm({});
  }, [open, initial]);

  if (!open) return null;

  const availableTypes = TYPES.filter((t) => (t.key === "bank" ? isManager : true));

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleCreate = async () => {
    setSubmitting(true);
    setError("");
    try {
      if (mode === "tour") {
        const payload = {
          name: form.name,
          destination: form.destination || "",
          start_date: form.start_date,
          end_date: form.end_date || form.start_date,
          status: form.status || "Onaylandı",
          capacity: Number(form.capacity || 20),
          sold: Number(form.sold || 0),
          revenue: Number(form.revenue || 0),
          cost: Number(form.cost || 0),
          operator: form.operator || "",
        };
        await api.post("/tours", payload);
      } else if (mode === "income" || mode === "expense") {
        const payload = {
          type: mode,
          title: form.title,
          category: form.category || (mode === "income" ? INCOME_CATEGORIES[0] : EXPENSE_CATEGORIES[0]),
          amount: Number(form.amount || 0),
          date: form.date || today(),
          account: form.account || accounts?.[0]?.name || "Merkez Kasa",
          status: form.status || "Bekliyor",
        };
        await api.post("/finance", payload);
      } else if (mode === "customer") {
        await api.post("/customers", {
          name: form.name,
          phone: form.phone || "",
          email: form.email || "",
          identity_no: form.identity_no || "",
          tour: form.tour || "",
          balance: Number(form.balance || 0),
          notes: form.notes || "",
        });
      } else if (mode === "bank") {
        await api.post("/bank-accounts", {
          name: form.name,
          type: form.type || "Banka",
          iban: form.iban || "",
          balance: Number(form.balance || 0),
          currency: "TRY",
        });
      }
      onCreated?.(mode);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kayıt oluşturulamadı.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderForm = () => {
    if (mode === "tour") {
      return (
        <div className="form-grid">
          <Field label="Tur adı" span={2}>
            <input value={form.name || ""} onChange={(e) => update("name", e.target.value)} placeholder="örn. Akdeniz Esintisi" data-testid="form-tour-name" required />
          </Field>
          <Field label="Destinasyon" span={2}>
            <input value={form.destination || ""} onChange={(e) => update("destination", e.target.value)} placeholder="Antalya · Kaş · Fethiye" data-testid="form-tour-destination" />
          </Field>
          <Field label="Başlangıç">
            <input type="date" value={form.start_date || today()} onChange={(e) => update("start_date", e.target.value)} data-testid="form-tour-start" required />
          </Field>
          <Field label="Bitiş">
            <input type="date" value={form.end_date || today()} onChange={(e) => update("end_date", e.target.value)} data-testid="form-tour-end" />
          </Field>
          <Field label="Kapasite">
            <input type="number" min="1" value={form.capacity || 20} onChange={(e) => update("capacity", e.target.value)} data-testid="form-tour-capacity" />
          </Field>
          <Field label="Satılan">
            <input type="number" min="0" value={form.sold || 0} onChange={(e) => update("sold", e.target.value)} data-testid="form-tour-sold" />
          </Field>
          <Field label="Satış tutarı (₺)">
            <input type="number" step="100" value={form.revenue || 0} onChange={(e) => update("revenue", e.target.value)} data-testid="form-tour-revenue" />
          </Field>
          <Field label="Maliyet (₺)">
            <input type="number" step="100" value={form.cost || 0} onChange={(e) => update("cost", e.target.value)} data-testid="form-tour-cost" />
          </Field>
          <Field label="Operatör" span={2}>
            <input value={form.operator || ""} onChange={(e) => update("operator", e.target.value)} placeholder="Tedarikçi / DMC" data-testid="form-tour-operator" />
          </Field>
          <Field label="Durum" span={2}>
            <select value={form.status || "Onaylandı"} onChange={(e) => update("status", e.target.value)} data-testid="form-tour-status">
              <option>Onaylandı</option>
              <option>Yaklaşıyor</option>
              <option>Beklemede</option>
              <option>Tamamlandı</option>
            </select>
          </Field>
        </div>
      );
    }
    if (mode === "income" || mode === "expense") {
      const categories = mode === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
      return (
        <div className="form-grid">
          <Field label="Başlık" span={2}>
            <input value={form.title || ""} onChange={(e) => update("title", e.target.value)} placeholder={mode === "income" ? "Örn. Kapadokya kaporası" : "Örn. Otel ön ödemesi"} data-testid="form-finance-title" required />
          </Field>
          <Field label="Tutar (₺)">
            <input type="number" step="50" value={form.amount || ""} onChange={(e) => update("amount", e.target.value)} data-testid="form-finance-amount" required />
          </Field>
          <Field label="Tarih">
            <input type="date" value={form.date || today()} onChange={(e) => update("date", e.target.value)} data-testid="form-finance-date" />
          </Field>
          <Field label="Kategori">
            <select value={form.category || categories[0]} onChange={(e) => update("category", e.target.value)} data-testid="form-finance-category">
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Hesap">
            <select value={form.account || accounts?.[0]?.name || ""} onChange={(e) => update("account", e.target.value)} data-testid="form-finance-account">
              {(accounts || []).map((a) => (
                <option key={a.id} value={a.name}>{a.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Durum" span={2}>
            <select value={form.status || "Bekliyor"} onChange={(e) => update("status", e.target.value)} data-testid="form-finance-status">
              <option>Bekliyor</option>
              <option>Tamamlandı</option>
            </select>
          </Field>
        </div>
      );
    }
    if (mode === "customer") {
      return (
        <div className="form-grid">
          <Field label="Ad Soyad" span={2}>
            <input value={form.name || ""} onChange={(e) => update("name", e.target.value)} data-testid="form-customer-name" required />
          </Field>
          <Field label="Telefon">
            <input value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} placeholder="+90 ..." data-testid="form-customer-phone" />
          </Field>
          <Field label="E-posta">
            <input type="email" value={form.email || ""} onChange={(e) => update("email", e.target.value)} data-testid="form-customer-email" />
          </Field>
          <Field label="TC / Pasaport">
            <input value={form.identity_no || ""} onChange={(e) => update("identity_no", e.target.value)} data-testid="form-customer-identity" />
          </Field>
          <Field label="Kayıtlı tur">
            <select value={form.tour || ""} onChange={(e) => update("tour", e.target.value)} data-testid="form-customer-tour">
              <option value="">— Seçilmedi —</option>
              {(tours || []).map((t) => (
                <option key={t.id} value={t.name}>{t.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Bakiye (₺)" span={2}>
            <input type="number" step="50" value={form.balance || 0} onChange={(e) => update("balance", e.target.value)} data-testid="form-customer-balance" />
          </Field>
          <Field label="Not" span={2}>
            <textarea rows="2" value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} data-testid="form-customer-notes" />
          </Field>
        </div>
      );
    }
    if (mode === "bank") {
      return (
        <div className="form-grid">
          <Field label="Hesap adı" span={2}>
            <input value={form.name || ""} onChange={(e) => update("name", e.target.value)} placeholder="Örn. Yapı Kredi · Vadesiz" data-testid="form-bank-name" required />
          </Field>
          <Field label="Tür">
            <select value={form.type || "Banka"} onChange={(e) => update("type", e.target.value)} data-testid="form-bank-type">
              <option>Banka</option>
              <option>Kasa</option>
              <option>Pos</option>
            </select>
          </Field>
          <Field label="Başlangıç bakiye (₺)">
            <input type="number" step="100" value={form.balance || 0} onChange={(e) => update("balance", e.target.value)} data-testid="form-bank-balance" />
          </Field>
          <Field label="IBAN" span={2}>
            <input value={form.iban || ""} onChange={(e) => update("iban", e.target.value)} placeholder="TR..." data-testid="form-bank-iban" />
          </Field>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" data-testid="new-record-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="close-modal">
          <X size={18} />
        </button>
        {!mode ? (
          <>
            <span className="eyebrow">HIZLI KAYIT</span>
            <h2>Yeni kayıt oluştur</h2>
            <p>Başlamak istediğin kaydı seç.</p>
            <div className="modal-options wide">
              {availableTypes.map(({ key, label, description, icon: Icon, testid }) => (
                <button key={key} onClick={() => setMode(key)} data-testid={testid}>
                  <Icon />
                  <b>{label}</b>
                  <small>{description}</small>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <span className="eyebrow">
              {TYPES.find((t) => t.key === mode)?.label.toUpperCase()}
            </span>
            <h2 className="form-title">Bilgileri doldur</h2>
            <p>Alanları tamamlayınca kaydet butonuna bas.</p>
            {renderForm()}
            {error && <div className="auth-error" style={{ marginTop: 12 }} data-testid="form-error">{error}</div>}
            <div className="form-actions">
              <button className="outline-button" onClick={() => setMode(null)} data-testid="form-back">
                ← Geri
              </button>
              <button className="primary-button" onClick={handleCreate} disabled={submitting} data-testid="form-submit">
                {submitting ? "Kaydediliyor…" : "Kaydet"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
